import { Share2, Users } from "lucide-react";
import type { ModuleAccessOrigin } from "@playbookai/types";
import { Badge } from "../../atoms/Badge/index.js";
import { Tooltip } from "../Tooltip/Tooltip.js";

export type AccessOrigin = ModuleAccessOrigin;

export const ACCESS_ORIGIN_FILTER_OPTIONS = [
  { value: "created", label: "Created by me" },
  { value: "shared", label: "Shared with me" },
] as const satisfies readonly {
  readonly value: AccessOrigin;
  readonly label: string;
}[];

export interface AccessOriginBadgeProps {
  readonly origin: AccessOrigin;
  /** Compact table indicator; the default retains the visible card label. */
  readonly variant?: "badge" | "icon";
}

/**
 * Compare the current viewer with the creator (or owner fallback supplied by
 * the caller). Unknown viewers have no access label; another creator is shared.
 */
export function accessOriginFor(
  currentUserId: string | null | undefined,
  creatorUserId: string,
): AccessOrigin | undefined {
  if (!currentUserId) return undefined;
  return currentUserId === creatorUserId ? "created" : "shared";
}

/**
 * Marks only shared items. Ownership is the ordinary state and needs no badge;
 * Cards retain a visible label; compact tables use a named people icon with
 * a hover explanation. Its accessible name is available without a tooltip.
 */
export function AccessOriginBadge({
  origin,
  variant = "badge",
}: AccessOriginBadgeProps) {
  if (origin === "created") return null;

  if (variant === "icon") {
    return (
      <Tooltip content="Shared with me" describeChild={false}>
        <span
          role="img"
          aria-label="Shared with me"
          className="inline-flex shrink-0 text-pb-text-muted"
        >
          <Users aria-hidden="true" className="h-4 w-4" />
        </span>
      </Tooltip>
    );
  }

  return (
    <Badge
      aria-label="Shared with me"
      title="Shared with me"
      className="shrink-0 gap-1.5 rounded-full px-2 py-1 text-dense-1 text-pb-text-secondary"
    >
      <Share2 aria-hidden="true" className="h-3 w-3 text-pb-text-muted" />
      <span>Shared</span>
      <span className="sr-only"> with me</span>
    </Badge>
  );
}
